import React, { useState, useCallback, useEffect } from 'react';
import './CreatePostPage.css';
import { API_BASE_URL } from '../api/config';


function CreatePostPage({ username, onPostCreated, onBack, onOpenChatRooms, onOpenProfile }) {
  const [title, setTitle] = useState('');
  const [priceCents, setPriceCents] = useState('');
  const [purchasePlace, setPurchasePlace] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [meetupPlace, setMeetupPlace] = useState('');
  const [headcount, setHeadcount] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [totalPrice, setTotalPrice] = useState('');
  const [pricePerPerson, setPricePerPerson] = useState(null);
  const [calculationError, setCalculationError] = useState('');
  const [splitMode, setSplitMode] = useState('ceil'); // ceil: 올림(추천) / floor: 내림

  const applySplitPrice = (result, mode) => {
    if (!result) return;
    const value =
      mode === 'ceil' && result.pricePerPersonCeil != null
        ? result.pricePerPersonCeil
        : result.pricePerPerson;
    setPriceCents(String(value));
  };

  const calculateGroupBuyPrice = useCallback(async () => {
    if (!totalPrice || !headcount) {
      setPricePerPerson(null);
      setCalculationError('');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/price/calculate?totalPrice=${totalPrice}&people=${headcount}`
      );
      const result = await response.json();

      if (result.success) {
        setPricePerPerson(result);
        const mode = result.remainder > 0 ? splitMode : 'floor';
        applySplitPrice(result, mode);
        setCalculationError('');
      } else {
        setCalculationError(result.message || '계산에 실패했습니다.');
        setPricePerPerson(null);
      }
    } catch (e) {
      setCalculationError('가격 계산에 실패했습니다.');
      setPricePerPerson(null);
    }
  }, [totalPrice, headcount, splitMode]);

  useEffect(() => {
    calculateGroupBuyPrice();
  }, [calculateGroupBuyPrice]);

  const handleCreate = async () => {
    setError('');
    if (!title.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('author', username);
      formData.append('title', title);
      formData.append('content', content);
      if (priceCents) formData.append('priceCents', priceCents);
      if (purchasePlace) formData.append('purchasePlace', purchasePlace);
      if (startDate) formData.append('startDate', startDate);
      if (endDate) formData.append('endDate', endDate);
      if (meetupPlace) formData.append('meetupPlace', meetupPlace);
      if (headcount) formData.append('headcount', headcount);
      if (image) formData.append('image', image);

      const res = await fetch(`${API_BASE_URL}/api/posts`, {
        method: 'POST',
        body: formData,
      });
      const result = await res.json();
      if (result && result.id) {
        onPostCreated(result);
      } else {
        setError(result?.message || result?.error || '게시물 생성에 실패했습니다.');
      }
    } catch (e) {
      setError('서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell createpost-page">
      <aside className="app-sidebar">
        <img src="/images/logoTextWhite.png" alt="공구나라 로고" className="app-sidebar-logo" />
        <nav className="app-sidebar-nav">
          {onOpenChatRooms && (
            <button type="button" className="app-nav-btn" onClick={onOpenChatRooms}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
              </svg>
              <span>내 채팅방</span>
            </button>
          )}
          {onOpenProfile && (
            <button type="button" className="app-nav-btn" onClick={onOpenProfile}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
              <span>내 프로필</span>
            </button>
          )}
        </nav>
      </aside>

      <main className="app-main">
        <div className="app-card createpost-card">
          <div className="app-card-header">
            {onBack && (
              <button type="button" className="app-back" onClick={onBack}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                <span>목록</span>
              </button>
            )}
            <h2 className="app-card-title">게시물 생성</h2>
          </div>

          <div className="app-card-body">
            <div className="form-group">
              <label htmlFor="create-title">제목</label>
              <input
                id="create-title"
                className="form-input"
                type="text"
                placeholder="어떤 공구인가요?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <section className="calculator-section">
              <h3 className="calculator-title">공구 가격 계산</h3>
              <p className="calculator-hint">총액과 인원을 넣으면 1인당 금액이 자동으로 채워집니다.</p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="create-total">총 가격 (원)</label>
                  <input
                    id="create-total"
                    className="form-input"
                    type="number"
                    min="0"
                    placeholder="예: 30000"
                    value={totalPrice}
                    onChange={(e) => setTotalPrice(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="create-headcount">공구 인원</label>
                  <input
                    id="create-headcount"
                    className="form-input"
                    type="number"
                    min="1"
                    placeholder="예: 3"
                    value={headcount}
                    onChange={(e) => setHeadcount(e.target.value)}
                  />
                </div>
              </div>

              {pricePerPerson && (
                <div className="calculation-result">
                  <div className="result-price">
                    1인당{' '}
                    {Number(
                      splitMode === 'ceil' && pricePerPerson.remainder > 0
                        ? pricePerPerson.pricePerPersonCeil
                        : pricePerPerson.pricePerPerson
                    ).toLocaleString()}
                    원
                  </div>
                  <div className="result-detail">
                    총 {Number(pricePerPerson.totalPrice).toLocaleString()}원 ÷ {pricePerPerson.people}명
                  </div>

                  {pricePerPerson.remainder > 0 && (
                    <div className="split-options">
                      <p className="split-hint">
                        나누어떨어지지 않아요. 어떻게 나눌까요?
                      </p>
                      <label className="split-option">
                        <input
                          type="radio"
                          name="splitMode"
                          checked={splitMode === 'ceil'}
                          onChange={() => {
                            setSplitMode('ceil');
                            applySplitPrice(pricePerPerson, 'ceil');
                          }}
                        />
                        <span>
                          올림 {Number(pricePerPerson.pricePerPersonCeil).toLocaleString()}원
                          <small> (여유 {pricePerPerson.surplusIfCeil}원 · 추천)</small>
                        </span>
                      </label>
                      <label className="split-option">
                        <input
                          type="radio"
                          name="splitMode"
                          checked={splitMode === 'floor'}
                          onChange={() => {
                            setSplitMode('floor');
                            applySplitPrice(pricePerPerson, 'floor');
                          }}
                        />
                        <span>
                          내림 {Number(pricePerPerson.pricePerPerson).toLocaleString()}원
                          <small> (나머지 {pricePerPerson.remainder}원 · 누군가 추가 부담)</small>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {calculationError && <div className="error-msg-small">{calculationError}</div>}
            </section>

            <div className="form-group">
              <label htmlFor="create-price">1인당 가격 (원)</label>
              <input
                id="create-price"
                className="form-input"
                type="number"
                min="0"
                placeholder="위에서 계산되거나 직접 입력"
                value={priceCents}
                onChange={(e) => setPriceCents(e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="create-purchase">구매처</label>
                <input
                  id="create-purchase"
                  className="form-input"
                  type="text"
                  placeholder="예: 쿠팡, 마켓컬리"
                  value={purchasePlace}
                  onChange={(e) => setPurchasePlace(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-place">만남 장소</label>
                <input
                  id="create-place"
                  className="form-input"
                  type="text"
                  placeholder="예: 역삼역 1번 출구"
                  value={meetupPlace}
                  onChange={(e) => setMeetupPlace(e.target.value)}
                />
              </div>
            </div>

            <div className="date-section">
              <label>공구 기간</label>
              <div className="date-inputs">
                <input
                  className="form-input date-input"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="시작일"
                />
                <span className="date-separator">~</span>
                <input
                  className="form-input date-input"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="종료일"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="create-content">내용</label>
              <textarea
                id="create-content"
                className="form-textarea"
                placeholder="상품 정보, 배송/수령 방법, 주의사항 등을 적어주세요."
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="create-image">이미지</label>
              <label htmlFor="create-image" className="file-drop">
                <input
                  id="create-image"
                  className="form-file-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImage(e.target.files[0] || null)}
                />
                <span className="file-drop-text">
                  {image ? image.name : '클릭해서 이미지를 선택하세요'}
                </span>
              </label>
              {image && <div className="file-name">선택됨: {image.name}</div>}
            </div>

            <button
              type="button"
              className="app-btn btn-submit"
              onClick={handleCreate}
              disabled={submitting}
            >
              {submitting ? '생성 중…' : '게시물 생성'}
            </button>

            {error && <div className="app-error">{error}</div>}
          </div>
        </div>
      </main>
    </div>
  );
}

export default CreatePostPage;
